import _extends from "@babel/runtime/helpers/extends";
import React, { useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles, useTheme } from '../../../utils/styles';
import Select from '../../forms/Select';
import styles from './styles';
const useStyles = createUseStyles(styles, 'SearchBar');
function SearchBar(_ref) {
  let {
    classes: classesProp,
    children,
    overrides: overridesProp,
    className: classNameProp,
    loadOptions,
    onChangeType,
    typeOptions,
    type,
    inputValue,
    onBlurSearch,
    useAsSimpleSearch,
    hideDropdownIndicator,
    isMulti,
    customOption,
    customTypeOption,
    keepInputValueOnBlur = true,
    forceBlurOnEnter = true,
    allowMultipleTypes = false,
    selectedTypesLiteral = '%@ Selected',
    selectedTypesPlaceholder = 'Selected...',
    hideSelectedOptions = false,
    shouldSetValueOnChange = false,
    focusDefaultOption = true,
    ...props
  } = _ref;
  const theme = useTheme();
  const classes = useClasses(useStyles, classesProp);
  const actionsControlRef = useRef([]);
  const defaultOverrides = useMemo(() => ({
    Select: {
      overrides: {
        control: {
          style: {
            backgroundColor: theme.colors.transparent,
            borderRadius: 0,
            borderColor: `${theme.colors.transparent} ${theme.colors.transparent} ${theme.colors.grey[800]} !important`
          }
        },
        controlFocused: {
          style: {
            backgroundColor: theme.colors.actionMinor[50]
          }
        },
        placeholder: {
          style: {
            color: theme.colors.grey[800]
          }
        }
      }
    }
  }), []);

  // Overrides
  const override = getOverrides({
    ...defaultOverrides,
    ...overridesProp
  }, SearchBar.overrides);

  // Classes
  const rootClassName = classnames(classes.root, classNameProp);
  const rootProps = {
    ...props,
    className: rootClassName
  };
  const TypeSelector = useMemo(() => {
    if (!onChangeType) return null;
    const typeClassname = classnames(classes.typeSelector, {
      [classes.typeSelectorWithValue]: !!type && !allowMultipleTypes
    });
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", _extends({
      className: classes.typeDivider
    }, override.typeDivider)), /*#__PURE__*/React.createElement(Select, _extends({
      className: typeClassname,
      onChange: onChangeType,
      options: typeOptions,
      value: type,
      onlyText: true,
      size: props.size,
      dropdownWidth: "250px",
      isSearchable: false,
      isMulti: allowMultipleTypes,
      customOption: customTypeOption,
      showNumSelected: allowMultipleTypes,
      numSelectedLiteral: selectedTypesLiteral,
      placeholder: selectedTypesPlaceholder,
      autoComplete: "nope" // must be an invalid value
      ,
      role: "representation",
      classes: {
        inputComponents: classes.typeSelectorInput,
        singleValue: classes.typeSingleValue,
        small: classes.typeSmall
      }
    }, override.TypeSelector)));
  }, [onChangeType, classes.typeSelector, classes.typeSelectorWithValue, classes.typeDivider, classes.typeSelectorInput, classes.typeSingleValue, classes.typeSmall, type, allowMultipleTypes, override.typeDivider, override.TypeSelector, typeOptions, props.size, customTypeOption, selectedTypesLiteral, selectedTypesPlaceholder]);

  // handleOnBlurSearch can be triggered in two differen scenarios
  // those scenarios are on blur through onInputCange and on press enter via onKeyDown
  const handleOnBlurSearch = useCallback((value, action) => {
    if (action.action && typeof action.action === 'string') {
      actionsControlRef.current.push(action.action);
      setTimeout(() => {
        if (action.action === 'input-blur') {
          if (!actionsControlRef.current.includes('set-value')) {
            onBlurSearch && onBlurSearch(value);
          }
          actionsControlRef.current = [];
        }
      });
    }
  }, [onBlurSearch]);
  return /*#__PURE__*/React.createElement("div", _extends({}, rootProps, override.root), /*#__PURE__*/React.createElement(Select, _extends({
    loadOptions: loadOptions,
    isFuzzy: true,
    afterControl: TypeSelector,
    isFullWidth: true,
    keepInputValueOnBlur: keepInputValueOnBlur || useAsSimpleSearch,
    forceBlurOnEnter: forceBlurOnEnter,
    useAsSimpleSearch: useAsSimpleSearch,
    hideDropdownIndicator: hideDropdownIndicator,
    onBlurSearch: handleOnBlurSearch,
    isMulti: isMulti,
    customOption: customOption,
    inputValue: inputValue,
    hideSelectedOptions: hideSelectedOptions,
    shouldSetValueOnChange: isMulti ? true : shouldSetValueOnChange,
    focusDefaultOption: focusDefaultOption,
    withoutFilter: true,
    autoComplete: "nope" // must be an invalid value
    ,
    role: "representation"
  }, override.Select, props)));
}
SearchBar.overrides = ['root', 'typeDivider', 'TypeSelector', 'Select'];
process.env.NODE_ENV !== "production" ? SearchBar.propTypes = {
  className: PropTypes.string,
  overrides: PropTypes.object,
  onChangeType: PropTypes.func,
  /** Allow multiple types selection */
  allowMultipleTypes: PropTypes.bool,
  selectedTypesLiteral: PropTypes.string,
  selectedTypesPlaceholder: PropTypes.string,
  typeOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any,
    isDisabled: PropTypes.bool,
    src: PropTypes.string,
    icon: PropTypes.element,
    iconType: PropTypes.string,
    subLabel: PropTypes.string
  })),
  type: PropTypes.any,
  inputValue: PropTypes.string,
  keepInputValueOnBlur: PropTypes.bool,
  forceBlurOnEnter: PropTypes.bool,
  onBlurSearch: PropTypes.func,
  /** It allows using the Select as a simple input for search uses */
  useAsSimpleSearch: PropTypes.bool,
  hideDropdownIndicator: PropTypes.bool,
  isMulti: PropTypes.bool,
  /** Function to customize the option row */
  customOption: PropTypes.func,
  /** Function to customize the option row in the optionType selector */
  customTypeOption: PropTypes.func,
  hideSelectedOptions: PropTypes.bool,
  /** If false, the selected value won't be set as selected. Useful if your goal is just to pick an option without showing it on the input */
  shouldSetValueOnChange: PropTypes.bool,
  /** Enable/disable focusing first option of the select */
  focusDefaultOption: PropTypes.bool
} : void 0;
export default /*#__PURE__*/React.memo(SearchBar);